import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useQuery } from 'react-query';
import { Breadcrumb, Button } from 'antd-v5';
import { DownloadOutlined, PlusOutlined } from '@ant-design/icons';
import InquiryFilters from './components/InquiryFilters';
import InquiryTable from './components/InquiryTable';
import ExportInquiriesModal from './components/ExportInquiriesModal';
import AddRolexInquiryModal from '../../../componentsV2/Rolex/AddRolexInquiryModal';
import { listInquiries } from '../../../api/vendors';
import { getAllMerchantTags } from '../../../services/clients';
import {
  transformInquiry,
  resolveInquirySort,
  flipSortOrder,
  DEFAULT_INQUIRY_SORT,
  INITIAL_INQUIRY_FILTERS,
  deserializeInquiryFilters,
  buildInquiryFilterQuery,
} from './utils';
import useDebounce from '../../../hooks/useDebounce';
import useSessionState from '../../../hooks/useSessionState';
import useVendorByKey from '../../../hooks/query/useVendorByKey';
import './styles.scss';

const RolexInquiries = ({ auth }) => {
  const { vendorId: rolexVendorId } = useVendorByKey('rolex');
  // Filters, sort and paging persist for the tab so they survive drilling into a client and coming back
  const storageKey = field => (auth.userId ? `rolex-inquiries:${auth.userId}:${field}` : null);
  const [filters, setFilters] = useSessionState(storageKey('filters'), INITIAL_INQUIRY_FILTERS, {
    deserialize: deserializeInquiryFilters,
  });
  const [page, setPage] = useSessionState(storageKey('page'), 1);
  const [pageSize, setPageSize] = useSessionState(storageKey('pageSize'), 25);
  const [sortBy, setSortBy] = useSessionState(storageKey('sortBy'), DEFAULT_INQUIRY_SORT.sortBy);
  const [sortOrder, setSortOrder] = useSessionState(storageKey('sortOrder'), DEFAULT_INQUIRY_SORT.sortOrder);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingInquiry, setEditingInquiry] = useState(null);
  const [tagLookup, setTagLookup] = useState([]);

  // Debounce text-typed search; dropdown filters change discretely so they
  // don't need this. The debounced value goes into the query key so
  // react-query handles request correlation (stale responses are dropped).
  // When the user clears the search (Reset filters or the input's X), the
  // immediate value is already empty — bypass the debounce in that case so
  // we don't fire an extra fetch with the stale prior search term.
  const debouncedClientSearch = useDebounce(filters.clientSearch, 500);
  const effectiveClientSearch = filters.clientSearch ? debouncedClientSearch : '';

  // Wrapper that updates filters and resets page atomically so only one fetch fires
  const updateFilters = useCallback(updater => {
    setFilters(prev => (typeof updater === 'function' ? updater(prev) : updater));
    setPage(1);
  }, []);

  // Server-side sort: map the antd column/direction to the backend sort contract
  // and reset to page 1 so the new ordering starts from the top of the dataset.
  const handleSortChange = useCallback((columnKey, antdOrder) => {
    if (antdOrder) {
      const next = resolveInquirySort(columnKey, antdOrder);
      setSortBy(next.sortBy);
      setSortOrder(next.sortOrder);
    } else {
      // antd's 3rd click clears the sort; flip the active column instead so the
      // initial Inquiry Date DESC can be toggled to ASC.
      setSortOrder(flipSortOrder);
    }
    setPage(1);
  }, []);

  // Fetch merchant tags for ID → name mapping
  useEffect(() => {
    getAllMerchantTags({ loadingStatusOff: true })
      .then(merchantTags => setTagLookup(merchantTags || []))
      .catch(() => {});
  }, []);

  const query = useMemo(
    () => ({ ...buildInquiryFilterQuery(filters, effectiveClientSearch), page, pageSize, sortBy, sortOrder }),
    [filters, effectiveClientSearch, page, pageSize, sortBy, sortOrder],
  );

  const {
    data: response,
    isFetching: loading,
    refetch,
  } = useQuery(['rolex-inquiries', query], () => listInquiries(query), {
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    initialData: { data: [], total: 0 },
  });

  const rawData = response?.data || [];
  const total = response?.total ?? rawData.length;

  // Apply tag/store name resolution at render time so changes to tagLookup
  // don't trigger a refetch
  const data = useMemo(() => {
    const stores = auth?.stores || [];
    return rawData.map(item => transformInquiry(item, { stores, tags: tagLookup }));
  }, [rawData, auth?.stores, tagLookup]);

  const isManagerOrAdmin = ['ADMIN', 'MANAGER'].includes(auth?.role);

  const canEditInquiry = useCallback(
    inquiry => {
      if (isManagerOrAdmin) return true;
      if (!auth?.userId || !inquiry?.associateId) return false;
      return String(inquiry.associateId) === String(auth.userId);
    },
    [isManagerOrAdmin, auth?.userId],
  );

  const handleEdit = inquiry => {
    setEditingInquiry(inquiry);
  };

  const editInitialModels = useMemo(() => editingInquiry?.models || [], [editingInquiry]);

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingInquiry(null);
  };

  const handleSaved = () => {
    handleCloseModal();
    refetch();
  };

  return (
    <div className="rolex-inquiries">
      <Breadcrumb className="rolex-inquiries__breadcrumb" items={[{ title: 'Rolex' }, { title: 'Inquiries' }]} />

      <div className="rolex-inquiries__header">
        <h1 className="rolex-inquiries__title">Inquiries</h1>
        <div className="rolex-inquiries__actions">
          <Button
            icon={<DownloadOutlined />}
            onClick={() => setShowExportModal(true)}
            data-testid="inquiries__page--export-button"
          >
            Export
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setShowAddModal(true)}
            data-testid="inquiries__page--add-button"
          >
            Add Inquiry
          </Button>
        </div>
      </div>

      <InquiryFilters
        filters={filters}
        setFilters={updateFilters}
        onReset={() => updateFilters(INITIAL_INQUIRY_FILTERS)}
      />

      <InquiryTable
        data={data}
        loading={loading}
        onEdit={handleEdit}
        canEdit={canEditInquiry}
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={(newPage, newPageSize) => {
          setPage(newPage);
          setPageSize(newPageSize);
        }}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortChange={handleSortChange}
        hasPos={!!auth?.pos_type}
      />

      <ExportInquiriesModal
        open={showExportModal}
        onClose={() => setShowExportModal(false)}
        filters={filters}
      />

      <AddRolexInquiryModal
        open={showAddModal || !!editingInquiry}
        onCancel={handleCloseModal}
        onSave={handleSaved}
        initialModels={editInitialModels}
        vendorId={rolexVendorId}
        inquiry={editingInquiry}
      />
    </div>
  );
};

const mapStateToProps = state => ({
  auth: state.auth,
});

export default connect(mapStateToProps)(RolexInquiries);
